// «Men necha foizda ishlayapman» — komissiya QOIDASINING o'zi.
//
// ⚠️ `commission-summary` BILAN CHALKASHTIRILMAYDI: u «qancha cashback
// qaytdi» degan YIG'INDI, bu esa «qanday hisoblanadi» degan QOIDA.
// Ikkalasi bir-birini tekshiradi va shuning uchun yonma-yon turadi —
// yig'indi qoidadan hisoblanmaydi (`CommissionSummary` javadoc'i:
// o'tmish qayta hisoblanmaydi, jurnaldan olinadi).

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, moneySchema, toMoney, uuidSchema } from '@/shared/api/schemas'
import type { CommissionRule } from './commissionRule'

/** Ekranda ham, hisobotda ham bir xil manzil — `SummaryCards` bilan bir qaror */
export const COMMISSION_RULES_ENDPOINT = 'GET /api/agents/{id}/commission-rules'

const commissionTierSchema = z.object({
  seq: z.number(),
  upToAmount: moneySchema.nullish(),
  percentBp: z.number().nullish(),
  fixedAmount: moneySchema.nullish(),
})

/*
  ⚠️ HAMMA JOYDA `.nullish()`, `.nullable()` EMAS.

  Servisda `default-property-inclusion: non_null` yoqilgan, ya'ni `null`
  maydon javobga UMUMAN yozilmaydi. `validTo` va `planId` shu sababdan
  javobda yo'q va ularni `=== null` bilan qidirish natijasiz qoladi.
  Panel VA'DAGA emas, KELGAN shaklga moslashadi.
*/
const commissionRuleSchema = z.object({
  id: uuidSchema,
  /** ⚠️ `z.enum(...)` EMAS — `CommissionRule.kind` izohidagi sabab */
  kind: z.string(),
  percentBp: z.number().nullish(),
  fixedAmount: moneySchema.nullish(),
  tiers: z.array(commissionTierSchema).nullish(),
  validFrom: instantSchema.nullish(),
  /**
   * ⚠️ OCHIQLIK MEZONI EMAS — `open` shu ishni qiladi. Bu maydon
   * BOSHQA savolga kerak: yopilish sanasi KELAJAKDA bo'lsa qoida BUGUN
   * hali kuchda (`activeRule`).
   */
  validTo: instantSchema.nullish(),
  open: z.boolean().nullish(),
})

/**
 * ⚠️ ALOHIDA EKSPORT — qorovul testi mantiqning nusxasini emas, SHUNI
 * chaqiradi (`tests/guards.mjs` §12).
 */
export function toCommissionRule(raw: z.infer<typeof commissionRuleSchema>): CommissionRule {
  return {
    id: raw.id,
    kind: raw.kind,
    percentBp: raw.percentBp ?? null,
    fixedAmount: raw.fixedAmount ? toMoney(raw.fixedAmount) : null,
    tiers: (raw.tiers ?? [])
      .map((tier) => ({
        seq: tier.seq,
        upToAmount: tier.upToAmount ? toMoney(tier.upToAmount) : null,
        percentBp: tier.percentBp ?? null,
        fixedAmount: tier.fixedAmount ? toMoney(tier.fixedAmount) : null,
      }))
      // ⚠️ `seq` bo'yicha TARTIBLANADI: pastki chegara oldingi pog'onadan
      // olinadi (`tierBounds`), ya'ni tartib buzilsa ekranda oraliqlar
      // aralashib ketardi. Server tartiblab beradi — bu ikkinchi qulf.
      .sort((a, b) => a.seq - b.seq),
    validFrom: raw.validFrom ?? '',
    validTo: raw.validTo ?? null,
    /*
      ⚠️ ZAXIRA: bayroq kelmasa YOPILISH SANASI o'qiladi. Jackson
      `boolean` ni tashlab ketmaydi, ya'ni `open` amalda har doim keladi —
      lekin u `isOpen()` getteridan hosil bo'ladi va serializatsiya
      sozlamasi o'zgarsa JIMGINA yo'qolishi mumkin. O'shanda `?? false`
      hamma qoidani yashirib, ekranga «qoida qo'yilmagan» degan yolg'onni
      chiqarardi; `?? true` esa yopilgan qoidani tirik ko'rsatardi.
      Ikkovi ham noto'g'ri, shuning uchun manba AYNAN o'sha maydon:
      serverda `open` — `validTo == null` ning o'zi.
    */
    open: raw.open ?? (raw.validTo ?? null) === null,
  }
}

export function parseCommissionRules(data: unknown): CommissionRule[] {
  return z.array(commissionRuleSchema).parse(data).map(toCommissionRule)
}

/**
 * `GET /api/agents/{id}/commission-rules` — sherik tokeni bilan ISHLAYDI
 * (`agent.read`, o'lchandi 2026-08-20).
 *
 * ⚠️ Javob — BUTUN TARIX, ro'yxat: yopilgan qoidalar ham qaytadi
 * (server qatorni o'chirmaydi, `valid_to` qo'yadi). «Hozir qanday»
 * degan savolga javob beradigani bittasi — `activeRule`.
 *
 * ⚠️ Bo'sh ro'yxat HAQIQIY holat va u JIMGINA qoldirilmaydi: ADR-032
 * o'lchoviga ko'ra Staging'da oltita agent aynan shunday edi — cashback
 * hisoblanmasdi va buni hech kim ko'rmasdi.
 *
 * ⚠️ `404` bu yerda «doiradan tashqarida» degani ham bo'lishi mumkin
 * (`requireVisible` 403 emas, 404 beradi) — `isEndpointMissing` ikkalasini
 * bir xil ko'rsatadi, chunki to'g'ri xulq bir xil: son o'ylab topilmaydi.
 */
export async function fetchCommissionRules(agentId: string): Promise<CommissionRule[]> {
  const response = await api.get<unknown>(`/api/agents/${agentId}/commission-rules`)
  return parseCommissionRules(response.data)
}
